import {useChatState} from '@github-ui/copilot-chat-contexts/ChatStateContext'
import {COPILOT_PATH, COPILOT_SPACES_PATH} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {useIsCopilotSpacesPage} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import {useCustomCopilotsEnabled} from '@github-ui/copilot-spaces/hooks'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {Icon} from '@github-ui/pacer/Icon'
import {Tooltip} from '@github-ui/pacer/Tooltip'
import {type LinkProps, SoftNavOnlyLink as Link, useLocation} from '@github-ui/react-router'
import {ArrowUpRightIcon, ChevronDownIcon, ChevronRightIcon, ComposeIcon, SpaceIcon} from '@primer/octicons-react'
import {IconButton, Label} from '@primer/react'
import {clsx} from 'clsx'
import {type ComponentType, type MouseEvent, type ReactNode, useCallback, useMemo, useState} from 'react'

import {useActivePlugin} from '../../hooks/use-active-plugin'
import {useNavigateToNewThread} from '../../hooks/use-navigate-to-new-thread'
import type {NavigationNestedItemsProps, NavigationTrailingAction} from '../../plugin/copilot-immersive-plugin'
import {usePlugins} from '../../plugin/ImmersivePluginsProvider'
import styles from './ServiceList.module.css'

type ServiceListItemProps = {
  href: string
  icon: React.ElementType
  onLinkClick?: (e: MouseEvent<HTMLElement>, page: string) => void
  id: string
  displayName: string
  condensed?: boolean
  label?: ReactNode
  trailingAction?: NavigationTrailingAction
  NestedItemsComponent?: ComponentType<NavigationNestedItemsProps>
  isActive?: boolean
  external?: boolean
  /**
   * Render as a plain `<a>` so the click triggers a real navigation. Use for hrefs that
   * sit outside the immersive Navigator's registered routes — a router `<Link>` to such an
   * href would cause `handleHistoryUpdate` to throw.
   */
  hardNavigation?: boolean
  apiURL?: string
  /**
   * A stringified JSON object that will be merged into the existing analytics data
   */
  'data-analytics-event'?: string
} & Pick<LinkProps, 'aria-current'>

function ServiceListItem({
  'aria-current': ariaCurrent,
  'data-analytics-event': dataAnalyticsEvent,
  href,
  icon,
  onLinkClick,
  id,
  displayName,
  condensed,
  label,
  trailingAction,
  NestedItemsComponent,
  isActive = ariaCurrent === 'page',
  external,
  hardNavigation,
  apiURL = '',
}: ServiceListItemProps) {
  const [expansionState, setExpansionState] = useState({isActive, isExpanded: isActive})
  if (expansionState.isActive !== isActive) {
    setExpansionState({isActive, isExpanded: isActive || expansionState.isExpanded})
  }
  const isExpanded =
    expansionState.isActive === isActive ? expansionState.isExpanded : isActive || expansionState.isExpanded
  const hasNestedItems = Boolean(NestedItemsComponent) && !condensed
  const isNestedExpanded = hasNestedItems && isExpanded
  const nestedDisclosureAriaCurrent = hasNestedItems && isActive && !isNestedExpanded ? 'page' : ariaCurrent
  const handleLinkClick = (e: MouseEvent<HTMLElement>) => {
    onLinkClick?.(e, id)
  }

  const handleToggleNested = () => {
    setExpansionState(previous => ({...previous, isExpanded: !previous.isExpanded}))
  }

  const commonLinkProps = {
    'aria-current': ariaCurrent,
    'data-analytics-event': dataAnalyticsEvent,
    className: styles.link,
    onClick: handleLinkClick,
  }

  const linkContent = hasNestedItems ? (
    <button
      type="button"
      aria-current={nestedDisclosureAriaCurrent}
      aria-expanded={isNestedExpanded}
      className={clsx(styles.link, styles.nestedLink)}
      onClick={handleToggleNested}
    >
      <span className={styles.iconSlot}>
        <Icon size={16} icon={icon} className={styles.leadingIcon} />
        <ChevronRightIcon size={12} className={styles.hoverChevron} />
        <ChevronDownIcon size={12} className={styles.expandedChevron} />
      </span>
      <div className={clsx(styles.content, condensed && styles.condensed)}>
        <div className={styles.textWrapper}>
          <div className={styles.text}>{displayName}</div>
        </div>
        <div className={styles.label}>{label}</div>
      </div>
    </button>
  ) : external ? (
    <a href={href} className={styles.link} aria-label={displayName} rel="noopener noreferrer">
      <Icon size={16} icon={icon} />
      <div className={clsx(styles.content, condensed && styles.condensed)}>
        <div className={styles.textWrapper}>
          <div className={styles.text}>{displayName}</div>
        </div>
        <div className={styles.label}>
          <div className={styles.externalLabel}>
            <span className={styles.previewLabel}>{label}</span>
            <ArrowUpRightIcon className={styles.externalIcon} />
          </div>
        </div>
      </div>
    </a>
  ) : hardNavigation ? (
    <a href={href} {...commonLinkProps} aria-label={`${displayName} (opens a new page)`}>
      <Icon size={16} icon={icon} />
      <div className={clsx(styles.content, condensed && styles.condensed)}>
        <div className={styles.textWrapper}>
          <div className={styles.text}>{displayName}</div>
        </div>
        <div className={styles.label}>
          <div className={styles.hardNavigationLabel}>
            {label}
            <ArrowUpRightIcon className={styles.hardNavigationIcon} aria-hidden="true" />
          </div>
        </div>
      </div>
    </a>
  ) : (
    <Link to={href} {...commonLinkProps} aria-label={displayName}>
      <Icon size={16} icon={icon} />
      <div className={clsx(styles.content, condensed && styles.condensed)}>
        <div className={styles.textWrapper}>
          <div className={styles.text}>{displayName}</div>
        </div>
        <div className={styles.label}>{label}</div>
      </div>
    </Link>
  )

  return (
    <li
      className={clsx(
        styles.section,
        hasNestedItems && styles.nestedSection,
        hasNestedItems && trailingAction && styles.hasTrailingAction,
      )}
    >
      <div className={styles.item} data-current={isActive ? 'true' : undefined}>
        {/* Avoid remounting which breaks CSS transitions */}
        <Tooltip text={condensed ? displayName : ''}>{linkContent}</Tooltip>
        {trailingAction && !condensed && trailingAction.hardNavigation && (
          <IconButton
            as="a"
            href={trailingAction.href}
            aria-label={trailingAction.label}
            icon={trailingAction.icon}
            variant="invisible"
            className={styles.trailingAction}
            onClick={e => {
              trailingAction.onClick?.(e)
              handleLinkClick(e)
            }}
          />
        )}
        {trailingAction && !condensed && !trailingAction.hardNavigation && (
          <IconButton
            as={Link}
            to={trailingAction.href}
            aria-label={trailingAction.label}
            icon={trailingAction.icon}
            variant="invisible"
            className={styles.trailingAction}
            onClick={e => {
              trailingAction.onClick?.(e)
              handleLinkClick(e)
            }}
          />
        )}
      </div>
      {NestedItemsComponent && !condensed && (
        <div>
          <NestedItemsComponent
            apiURL={apiURL}
            isExpanded={isNestedExpanded}
            isSidebarCondensed={Boolean(condensed)}
            onNavigate={handleLinkClick}
          />
        </div>
      )}
    </li>
  )
}

interface ServiceListProps {
  toggleFloatingSidebar: () => void
  isSidebarCondensed: boolean
}

interface SortableItem {
  type: 'spaces' | 'plugin'
  component: ReactNode
  displayName: string
}

const serviceListAnalyticsData = JSON.stringify({source: 'ServiceList'})

function getPluginAriaCurrent({
  hasNestedItems,
  isActive,
  isExactPath,
}: {
  hasNestedItems: boolean
  isActive: boolean
  isExactPath: boolean
}) {
  if (hasNestedItems) return isExactPath ? 'page' : undefined
  return isActive ? 'page' : undefined
}

export function ServiceList({toggleFloatingSidebar, isSidebarCondensed}: ServiceListProps) {
  const chatState = useChatState()
  const {selectedThreadID} = chatState
  const activePluginId = useActivePlugin()?.id
  const location = useLocation()
  const plugins = usePlugins()
  const navigateToNewThread = useNavigateToNewThread()
  const isCustomCopilotsEnabled = useCustomCopilotsEnabled()

  const isSpacesPage = useIsCopilotSpacesPage()
  const isMainPage = !selectedThreadID && !activePluginId && !isSpacesPage

  const handleNewThread = async (e: MouseEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.metaKey || e.ctrlKey) return

    toggleFloatingSidebar()
    await navigateToNewThread({clearTopic: true, includeThreads: false})

    sendEvent('dotcom_chat.activate', {target: 'SIDEBAR_CONVERSATION_NEW', mode: 'immersive'})
  }

  const handleSpacesNavigate = useCallback(
    (e: MouseEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.metaKey || e.ctrlKey) return

      toggleFloatingSidebar()
    },
    [toggleFloatingSidebar],
  )

  const handlePluginNavigate = useCallback(
    (e: MouseEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.metaKey || e.ctrlKey) return

      toggleFloatingSidebar()
    },
    [toggleFloatingSidebar],
  )

  const showSpacesPreviewLabel = !isFeatureEnabled('copilot_spaces_ga')

  const sortedItems = useMemo(() => {
    const spacesItem: SortableItem[] = isCustomCopilotsEnabled
      ? [
          {
            type: 'spaces',
            component: (
              <ServiceListItem
                key="spaces"
                aria-current={isSpacesPage ? 'page' : undefined}
                data-analytics-event={serviceListAnalyticsData}
                displayName="Spaces"
                href={COPILOT_SPACES_PATH}
                icon={SpaceIcon}
                condensed={isSidebarCondensed}
                id="spaces"
                onLinkClick={handleSpacesNavigate}
                label={showSpacesPreviewLabel && <Label variant="success">Preview</Label>}
              />
            ),
            displayName: 'Spaces',
          },
        ]
      : []

    const pluginItems = plugins.flatMap(p => {
      if (p.navigationItems && p.navigationItems.length > 0) {
        return p.navigationItems.map(item => {
          const isActive = activePluginId === p.id
          return {
            type: 'plugin' as const,
            component: (
              <ServiceListItem
                key={`${p.id}-${item.id}`}
                aria-current={getPluginAriaCurrent({
                  hasNestedItems: Boolean(item.NestedItemsComponent) && !isSidebarCondensed,
                  isActive,
                  isExactPath: location.pathname === item.href,
                })}
                data-analytics-event={serviceListAnalyticsData}
                displayName={item.displayName}
                href={item.href}
                icon={item.icon}
                condensed={isSidebarCondensed}
                id={item.id}
                onLinkClick={item.external || item.hardNavigation ? undefined : handlePluginNavigate}
                label={item.label}
                trailingAction={item.trailingAction}
                NestedItemsComponent={item.NestedItemsComponent}
                isActive={isActive}
                external={item.external}
                hardNavigation={item.hardNavigation}
                apiURL={chatState.apiUrl}
              />
            ),
            displayName: item.displayName,
          }
        })
      }

      return []
    })

    const sorted = [...spacesItem, ...pluginItems].sort((a, b) => {
      return a.displayName.localeCompare(b.displayName)
    })

    return sorted
  }, [
    isCustomCopilotsEnabled,
    isSpacesPage,
    isSidebarCondensed,
    handleSpacesNavigate,
    showSpacesPreviewLabel,
    plugins,
    chatState.apiUrl,
    activePluginId,
    handlePluginNavigate,
    location.pathname,
  ])

  const items = sortedItems.map(item => item.component)
  const chatItem = (
    <ServiceListItem
      key="home"
      aria-current={isMainPage ? 'page' : undefined}
      data-analytics-event={serviceListAnalyticsData}
      displayName="New chat"
      href={COPILOT_PATH}
      icon={ComposeIcon}
      condensed={isSidebarCondensed}
      id="home"
      onLinkClick={handleNewThread}
    />
  )

  return <ul className={styles.list}>{[chatItem, ...items]}</ul>
}
